import React, { useCallback, useContext } from 'react';
import type { GridApi } from 'ag-grid-community';

import AnalyticsContext from 'contexts/AnalyticsContext';
import ConfirmationModal from 'components/ConfirmationModal';
import downloadTableCsv from 'components/DataTable/downloadTableCsv';
import ExportButton from 'components/Grid/ExportButton/ExportButton';
import useGridCsvExport, {
  processCellCallback,
} from 'components/Grid/ExportButton/useGridCsvExport';
import useIsLiveBonusPeriodExport from 'components/Grid/ExportButton/useIsLiveBonusPeriodExport';
import usePopup from 'hooks/usePopup';
import useValueFormatters from 'hooks/useValueFormatters';

/**
 * Export for boards whose grid is aggregated client-side (sign-off boards of
 * sub-period programmes — see useShouldAggregateSignOff). The aggregated
 * datasource already holds every driver row, so this downloads the grid's rows
 * as a CSV via ag-grid rather than going through the search2-oriented export,
 * whose loaded-vs-total document check doesn't apply here. When DataTable
 * draws the grid, its export handle stands in for the grid api.
 */
const AggregatedBoardCsvExportButton = ({
  gridApi,
  exportHandle,
  exportName,
}: {
  gridApi?: Pick<GridApi, 'exportDataAsCsv'>;
  exportHandle?: DataTable.ExportHandle;
  exportName: string;
}) => {
  const { isOpen, open, close } = usePopup();
  const { trackEvent } = useContext(AnalyticsContext);
  const { formatValue, formatField } = useValueFormatters();
  const { getFileName, domId } = useGridCsvExport({ exportName });
  const isLiveBonusPeriodExport = useIsLiveBonusPeriodExport();

  const downloadCsv = useCallback(() => {
    trackEvent('Grid - Exported', { name: exportName });
    const processCell = processCellCallback(formatValue, formatField);
    if (exportHandle) {
      downloadTableCsv({
        cells: exportHandle.getCells({
          processCell,
          isTotalsRowIncluded: false,
        }),
        fileName: getFileName(),
      });
      return;
    }
    if (gridApi) {
      gridApi.exportDataAsCsv({
        fileName: getFileName(),
        skipPinnedBottom: true,
        processCellCallback: processCell,
      });
    }
  }, [
    exportHandle,
    exportName,
    formatField,
    formatValue,
    getFileName,
    gridApi,
    trackEvent,
  ]);

  return (
    <>
      <ExportButton
        id={domId}
        onClick={isLiveBonusPeriodExport ? open : downloadCsv}
      />
      <ConfirmationModal
        isOpen={isOpen}
        close={close}
        title={'Export Grid?'}
        body={
          'Bonus payments should not be made until the cycle is closed and signed off'
        }
        confirmText="Export"
        onConfirmed={downloadCsv}
      />
    </>
  );
};

export default AggregatedBoardCsvExportButton;
